'use client';

import { useFirstInteraction } from '@/contexts/FirstInteractionContext';
import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { Component, type ReactNode, type ErrorInfo } from 'react';

// Error boundary specifically for deferred scripts - fail silently
class DeferredScriptErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console but don't trigger app error boundary
    console.warn('[DeferredScripts] Non-critical script failed to load:', error.message, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return null; // Fail silently
    }
    return this.props.children;
  }
}

// Dynamically import SpeedInsights only after interaction with retry logic
const SpeedInsights = dynamic(
  () => import('@vercel/speed-insights/next')
    .then(mod => mod.SpeedInsights)
    .catch(err => {
      console.warn('[SpeedInsights] Failed to load, will retry on next navigation:', err.message);
      // Return a dummy component that renders nothing
      return () => null;
    }),
  { 
    ssr: false,
    loading: () => null,
  }
);

// Dynamically import Intercom only after interaction
const Intercom = dynamic(
  () => import('@/components/Intercom')
    .then(mod => ({ default: mod.Intercom }))
    .catch(err => {
      console.warn('[Intercom] Failed to load:', err.message);
      return { default: () => null };
    }),
  { 
    ssr: false,
    loading: () => null,
  }
);

// Dynamically import FullStory only after interaction
const FullStory = dynamic(
  () => import('@/components/FullStory')
    .then(mod => ({ default: mod.FullStory }))
    .catch(err => {
      console.warn('[FullStory] Failed to load:', err.message);
      return { default: () => null };
    }),
  { 
    ssr: false,
    loading: () => null,
  }
);

/**
 * Component that defers loading of heavy JavaScript until after first user interaction
 * This improves initial page load performance and Core Web Vitals
 */
export function DeferredScripts() {
  const hasInteracted = useFirstInteraction();
  const [isMounted, setIsMounted] = useState(false);

  // Prevent hydration mismatch - only render after client mount
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Only render deferred scripts after interaction AND after mount
  if (!hasInteracted || !isMounted) {
    return null;
  }

  return (
    <DeferredScriptErrorBoundary>
      <SpeedInsights />
      <Intercom />
      <FullStory />
    </DeferredScriptErrorBoundary>
  );
}
